import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../api/client';
import { registerForPush, unregisterFromPush } from '../services/push';

interface User {
  id: string;
  email: string;
  role: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;

  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<{ message: string }>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string, user: User) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,

      login: async (email, password) => {
        const { data } = await api.post<{
          accessToken: string;
          refreshToken: string;
          user: User;
        }>('/auth/login', { email, password });

        set({
          user: data.user,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          isAuthenticated: true,
        });

        // Set token in axios defaults
        api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

        /* Now, and not before: the push token is stored against a user, and
           iOS gives you one permission prompt — spending it on a stranger who
           has not signed in wastes it. Not awaited, because a permission
           dialog must never sit between her and her home screen; it swallows
           its own failures. */
        void registerForPush();
      },

      register: async (email, password) => {
        const currentLocale = localStorage.getItem('maternally-locale') ?? 'en';
        const { data } = await api.post<{ message: string }>('/auth/register', {
          email,
          password,
          language: currentLocale,
        });
        return data;
      },

      logout: async () => {
        /* Withdraw the device BEFORE the tokens go, while the request can
           still authenticate. Left registered, the next crisis alert for this
           account lands on a handset someone else may be holding. */
        await unregisterFromPush();
        try {
          const { refreshToken } = get();
          await api.post('/auth/logout', { refreshToken });
        } catch {
          // Always clear local state
        } finally {
          get().clearAuth();
        }
      },

      refreshAccessToken: async () => {
        const { refreshToken } = get();
        if (!refreshToken) throw new Error('No refresh token');

        const { data } = await api.post<{
          accessToken: string;
          refreshToken: string;
        }>('/auth/refresh', { refreshToken });

        set({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        });

        api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
      },

      setTokens: (accessToken, refreshToken, user) => {
        set({ accessToken, refreshToken, user, isAuthenticated: true });
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      },

      clearAuth: () => {
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
        delete api.defaults.headers.common['Authorization'];
      },
    }),
    {
      name: 'maternally-auth',
      // Only persist token — don't persist user object (re-fetch from server)
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
      }),
    }
  )
);
